import React from "react";

export type QuestionsChallengeErrorDisplay = {
  name: string;
  description?: string | React.ReactNode;
};

const ESIGN_ERROR_MESSAGES: Record<string, QuestionsChallengeErrorDisplay> = {
  operationFailed: {
    name: "La operación falló",
    description:
      "No pudimos completar la validación por un error con el proveedor de Firma Electrónica. Intenta nuevamente.",
  },
  validationFailed: {
    name: "No pudimos validar su identidad",
    description:
      "El servicio de validación no está disponible (proveedor de firma electrónica avanzada). Por favor intenta más tarde.",
  },
  deceasedPerson: {
    name: "Nº de documento inválido",
    description: (
      <>
        Documento registrado como{" "}
        <span style={{ fontWeight: 600 }}>persona fallecida</span>. Si crees que
        es un error, revisa la información con el Registro Civil.
      </>
    ),
  },
  documentNumberMismatch: {
    name: "Nº de documento no coincide con el rut registrado",
  },
  invalidDocumentNumber: {
    name: "La cédula no está vigente o no corresponde al RUT registrado",
  },
  exceededMaxRetries: {
    name: "Excediste el número de intentos",
    description: (
      <>
        Puedes{" "}
        <span style={{ fontWeight: 600 }}>
          completar la verificación con otro método de autenticación
        </span>{" "}
        o volver al inicio e intentar más tarde
      </>
    ),
  },
  unableToGenerateQuestions: {
    name: "No fue posible generar las preguntas",
    description: (
      <>
        El Registro Civil no cuenta con la información suficiente para generar
        el cuestionario. Puedes{" "}
        <span style={{ fontWeight: 600 }}>
          completar la verificación con otro método de autenticación
        </span>{" "}
        o volver al inicio e intentar más tarde.
      </>
    ),
  },
  underagePerson: {
    name: "Nº de documento inválido",
    description: (
      <>
        El documento corresponde a un{" "}
        <span style={{ fontWeight: 600 }}>menor de edad</span>. Si crees que es
        un error, revisa la información con el Registro Civil.
      </>
    ),
  },
  incompleteDataRequired: {
    name: "Se requiere información adicional",
    description:
      "Algunos datos del Registro Nacional de Prestadores Individuales de Salud no fueron encontrados. Por favor, completa los nuevos campos requeridos.",
  },
  unexpectedError: {
    name: "Error inesperado",
    description:
      "No pudimos completar la validación por un error temporal. Intenta nuevamente en unos minutos; si persiste, vuelve más tarde.",
  },
  wrongAnswers: {
    name: "Algunas respuestas estaban incorrectas. Inténtalo nuevamente",
  },
  timeLimitExceeded: {
    name: "Tiempo agotado. Inténtalo nuevamente",
  },
};

const BPO_ERROR_MESSAGES: Record<string, QuestionsChallengeErrorDisplay> = {
  vimpIncorrectAnswer: ESIGN_ERROR_MESSAGES.wrongAnswers,
  vimpMaxAttemptsExceeded: ESIGN_ERROR_MESSAGES.exceededMaxRetries,
  invalidSerial: ESIGN_ERROR_MESSAGES.documentNumberMismatch,
  invalidUserData: {
    name: "No pudimos validar los datos de tu cuenta",
    description:
      "El proveedor de firma electrónica rechazó el RUT o el correo registrados en tu cuenta. Contáctanos para ayudarte.",
  },
  vimpClaveUnicaMissing: ESIGN_ERROR_MESSAGES.incompleteDataRequired,
  invalidPin: {
    name: "El PIN no cumple el formato requerido",
    description:
      "Crea un PIN nuevo que cumpla con los requisitos indicados e intenta nuevamente.",
  },
  providerError: ESIGN_ERROR_MESSAGES.operationFailed,
  authError: ESIGN_ERROR_MESSAGES.validationFailed,
  certificateValidationsExpired: {
    name: "Tus validaciones expiraron, debes repetirlas",
    description:
      "Pasó demasiado tiempo desde que validaste tu identidad. Vuelve a completar las validaciones para generar tu firma.",
  },
  certificateNotFound: {
    name: "No encontramos tu certificado",
    description:
      "No pudimos encontrar tu certificado de firma electrónica. Intenta nuevamente.",
  },
  emailNotFound: ESIGN_ERROR_MESSAGES.incompleteDataRequired,
  certificatePendingClaveUnica: {
    name: "Debes volver a validar tu identidad con ClaveÚnica",
    description:
      "Necesitamos que inicies sesión con ClaveÚnica nuevamente para generar tu firma.",
  },
  certificatePendingVimp: {
    name: "Debes volver a responder las preguntas",
    description:
      "Necesitamos que respondas nuevamente el cuestionario de identidad para generar tu firma.",
  },
  claveUnicaNotValid: {
    name: "No alcanzamos a validar tu ClaveÚnica. Intenta nuevamente.",
    description:
      "Debes completar el inicio de sesión en ClaveÚnica para continuar con la validación de tu identidad.",
  },
  ciDocNumMissing: {
    name: "Necesitamos el número de tu documento",
    description:
      "Ingresa el número de serie o documento de tu cédula de identidad para continuar.",
  },
  providerStateMissing: ESIGN_ERROR_MESSAGES.unexpectedError,
  vimpValidationFailed: ESIGN_ERROR_MESSAGES.unexpectedError,
};

export const QUESTIONS_CHALLENGE_ERROR_MESSAGES: Record<
  string,
  QuestionsChallengeErrorDisplay
> = {
  ...ESIGN_ERROR_MESSAGES,
  ...BPO_ERROR_MESSAGES,
};

const DEFAULT_MESSAGE = QUESTIONS_CHALLENGE_ERROR_MESSAGES.unexpectedError;

export function hasQuestionsChallengeErrorMessage(
  name: string | undefined,
): boolean {
  return !!name && name in QUESTIONS_CHALLENGE_ERROR_MESSAGES;
}

export function getQuestionsChallengeErrorDisplay(
  name: string | undefined,
): QuestionsChallengeErrorDisplay {
  if (!name) return DEFAULT_MESSAGE;
  return QUESTIONS_CHALLENGE_ERROR_MESSAGES[name] ?? DEFAULT_MESSAGE;
}
